import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import estimationsModule from '../../../components/estimations';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import timeTrackingModel from '$$timeTrackingModel';
import app from '$$app';
import reportHelpers from '../include/helpers';
import globalToast from '$$vueCmp/toast';
import { ColorCycler } from '../include/helpers';

class BudgetByResourceReport {
  constructor() {
    this.costWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
  }

  initCostWorker() {
    if (window.Worker && !this.costWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.costWorker = new Worker('/workers/costs_worker.js' + `?${version}`);
    }

    this._initEvents();
  }

  _initEvents() {
    const that = this;

    this.costWorker.onmessage = function (e) {
      const updatedCosts = e.data;

      Object.entries(updatedCosts).forEach(([key, updates]) => {
        if (updates.length) {
          const tasks = that.reportTasks[key]?.tasks;

          if (Array.isArray(tasks)) {
            that.reportTasks[key].tasks = tasks.map(task => {
              const update = updates.find(obj => obj.id === task.id);

              if (update) {
                return {
                  ...task,
                  cost: update.cost,
                  actual_cost: update.actual_cost,
                };
              }

              return task;
            });
          }
        }
      });

      that.initReportData();
    };

    this.costWorker.onerror = function (e) {
      globalToast.error(__('costs_calculating_error'));
    };
  }

  calculateCostsByWorker(projectIds, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    if (projectIds?.length) {
      projectIds.forEach(projectId => {
        const projectResources = Store.getters['resourcesModel/getResourcesByProjectId'](projectId);

        if (projectResources?.length) {
          projectResources.forEach(res => {
            const tasksByResource = Store.getters['tasksModel/getTasksByResourceIdAndGanttId'](res.id, projectId);
            const totalEstimateData = Store.getters['tasksModel/getTotalEstimateDataForProject'](projectId);
            const preparedTasksForWorker = this.prepareTasksForWorker(tasksByResource);

            const filteredTasks = preparedTasksForWorker.filter(task => reportHelpers.returnTaskByRange({
              ...task,
              start_date: task.task_data.start_date,
              end_date: task.task_data.end_date,
            }, filterRange));

            const rangeForWorker = {
              start: currentFilter?.properties?.rangeDate?.start || totalEstimateData.start_date,
              end: currentFilter?.properties?.rangeDate?.end || totalEstimateData.end_date,
            };

            this.reportTasks[`resourceId:${res.id}-projectId:${projectId}`] = { tasks: filteredTasks, range: rangeForWorker };
          });
        }
      });

      if (baselineTasks?.length) {
        this.prepareBaselineTasksForWorker(baselineTasks, filterRange);
      }
    }

    this.sendDataToWorker(this.reportTasks);
  }

  prepareBaselineTasksForWorker(baselineTasks, filterRange) {
    const currentFilter = Store.getters['filter/currentFilter'];
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    const rangeForWorker = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };

    baselineTasksAndMilestones.forEach(task => {
      if (task.resources && task.resources.length) {
        task.resources.forEach(res => {
          const filteredTask = reportHelpers.returnTaskByRange(task, filterRange);

          if (filteredTask) {
            const resource = Store.getters['resourcesModel/getResourceById'](res.resource_id);
            const preparedBaselineTaskForWorker = this.prepareBaselineTaskForWorker(task, resource);

            this.reportTasks[`baseline-${res.resource_id}-${task.id}`] = { tasks: [preparedBaselineTaskForWorker], range: rangeForWorker };
          }
        });
      }
    });
  }

  sendDataToWorker(tasksData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.costWorker.postMessage(_.cloneDeep({
      projectConfigs: reportHelpers.prepareProjectConfigs(),
      projectSettings: projectSettingsMap,
      customDaysData: customDayTemplatesModel.serialize(),
      tasksData,
      resourcesData: Store.getters['resourcesModel/getAllResources'],
    }));
  }

  prepareTasksForWorker(tasksByResource) {
    const resultData = [];

    _.each(tasksByResource, resourceTaskData => {
      const projectData = projectsModel.getProjectDataById(resourceTaskData.gantt_id);
      const originalTaskData = Store.getters['tasksModel/getTaskByGanttId'](resourceTaskData.gantt_id, resourceTaskData.task_id);
      const resource = Store.getters['resourcesModel/getResourceById'](resourceTaskData.resource_id);
      const logsData = timeTrackingModel.getLogsByResourceId(resourceTaskData.resource_id);
      const taskLogsData = logsData?.length ? logsData.filter(log => log.task_id === resourceTaskData.task_id) : [];

      resultData.push({
        id: originalTaskData.id,
        gantt_id: originalTaskData.gantt_id,
        task_id: originalTaskData.id, // do not remove
        project_name: projectData.name,
        task_name: originalTaskData.text,
        task_data: originalTaskData,
        info_text: originalTaskData.note,
        resources: [resource],
        resource_id: String(resourceTaskData.resource_id),
        resource_data: [resource],
        cost: estimationsModule.calculateCostChildTask(originalTaskData) || 0,
        actual_cost: estimationsModule.calculateActualCostChildTask(originalTaskData) || 0,
        cost_range_start: originalTaskData.start_date,
        cost_range_end: originalTaskData.end_date,
        logsData: taskLogsData,
      });
    });

    return resultData;
  }

  prepareBaselineTaskForWorker(baselineTask, resource) {
    const projectData = projectsModel.getProjectDataById(baselineTask.gantt_id);
    const logsData = timeTrackingModel.getLogsByResourceId(resource.id);
    const taskLogsData = logsData?.length ? logsData.filter(log => log.task_id === baselineTask.id) : [];

    return {
      id: baselineTask.id,
      gantt_id: baselineTask.gantt_id,
      task_id: baselineTask.id, // do not remove
      project_name: projectData.name,
      task_name: baselineTask.text,
      task_data: baselineTask,
      info_text: baselineTask.note,
      resources: [resource],
      resource_id: String(baselineTask.resource_id),
      resource_data: [resource],
      cost: estimationsModule.calculateCostChildTask(baselineTask) || 0,
      actual_cost: estimationsModule.calculateActualCostChildTask(baselineTask) || 0,
      cost_range_start: baselineTask.start_date,
      cost_range_end: baselineTask.end_date,
      logsData: taskLogsData,
    };
  }

  prepareTableTasks() {
    const tableTasks = [];
    const reportTasksByAssignee = _.pickBy(this.reportTasks, (v, k) => !k.startsWith('baseline-'));

    Object.entries(reportTasksByAssignee).forEach(([key, data]) => {
      const resourceId = key.split('-')[0].split(':')[1];

      data.tasks.forEach(task => {
        const baselineData = this.reportTasks[`baseline-${resourceId}-${task.id}`];
        const baselineTaskCost = baselineData?.tasks[0]?.cost || 0;
        let variance = 0;

        if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
          variance = task.cost - task.actual_cost;
        }

        if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
          variance = baselineTaskCost - task.actual_cost;
        }

        if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
          variance = baselineTaskCost - task.cost;
        }

        const preparedTask = {
          id: task.id,
          task: { id: task.id, name: task.task_name },
          project: { id: task.gantt_id, name: task.project_name, color: ColorCycler.getColor(task.gantt_id) },
          assignee: Store.getters['resourcesModel/getResourceById'](+resourceId),
          baseline_budget: baselineTaskCost,
          budget: task.cost,
          actual_cost: task.actual_cost,
          variance,
        };

        tableTasks.push(preparedTask);
      });
    });

    return tableTasks;
  }

  initReportData() {
    const tableTasks = this.prepareTableTasks();

    app.trigger('onAfterResourcesCostsCalculation', {
      table: tableTasks,
    });
  }
}

const budgetByResource = new BudgetByResourceReport();

export default budgetByResource;
